import AppContext from '@/AppContext';
import { useAsyncEffect } from '@/hooks';
import { paginate } from '@/lib/utils';
import { useStores } from '@/store';
import { observer } from 'mobx-react';
import React, { useContext, useState } from 'react';
import { Link } from 'react-router';
import { Form, Grid, Pagination } from 'semantic-ui-react';
import { EmployeeStatus, Moment } from 'sol-data';
import { EmployeeStatusColor } from '../DepartmentEmployeesList/utils';
import { EntityList } from '../EntityList';
import { Item, ListColumn } from '../ListLayout';
import { SignatureModal } from '../SignatureModal/SignatureModal';
import { setISODay } from 'date-fns';

const RESULTS_PER_PAGE = 10;

interface OnboardingDocumentItem extends Item {
  id: number;
  deleteDatetime?: Moment;
  title: string;
  createdBy: string;
  createdOn: string;
  signedAt: string | undefined;
  signature: string | undefined;
  status: EmployeeStatus;
}

interface EmployeeOnboardingDocumentsProps {
  employeeId: number;
}

export const EmployeeOnboardingDocuments = observer(
  ({ employeeId }: EmployeeOnboardingDocumentsProps) => {
    const { onboardingDocumentStore } = useStores();
    const [currentPage, setCurrentPage] = useState(1);
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [signature, setSignature] = useState<string>();
    const appContext = useContext(AppContext);
    const isAdmin = appContext?.isAdmin;

    const fetchData = async (shouldIncludeDeleted: boolean) => {
      await onboardingDocumentStore.fetchAllForEmployee(employeeId!, shouldIncludeDeleted);
    };

    useAsyncEffect(async () => {
      await fetchData(includeDeleted);

      return () => {
        onboardingDocumentStore.clearFilters();
        onboardingDocumentStore.clear();
      };
    }, [employeeId]);

    const onboardingDocuments = onboardingDocumentStore.filtered.map<OnboardingDocumentItem>(
      ({
        id,
        title,
        createdByUser,
        insertDatetime,
        deleteDatetime,
        fileName,
        azureBlobName,
        employees,
      }) => {
        return {
          id,
          deleteDatetime,
          title,
          createdBy: createdByUser?.fullName ?? 'Anonymous',
          createdOn: insertDatetime.format('MMMM Do [at] h:mmA'),
          signedAt: employees?.length
            ? employees[0].signedAt?.format('MMMM Do [at] h:mmA')
            : undefined,
          signature: employees?.length ? employees[0].signature : undefined,
          fileName,
          azureBlobName,
          status:
            employees?.length && employees[0]?.isCompleted
              ? EmployeeStatus.COMPLETE
              : EmployeeStatus.INCOMPLETE,
        };
      },
    );

    const columns: ListColumn<OnboardingDocumentItem>[] = [
      {
        name: 'Document Title',
        key: 'title',
        width: { sm: 6, lg: 6 },
        render: ({ id, title }) => (
          <Link to={`/onboarding/documents/templates/${id}`} target="_blank">
            {title}
          </Link>
        ),
      },
      {
        name: 'Created By',
        key: 'createdBy',
        textAlign: 'center',
        width: { sm: 2, lg: 2 },
        render: ({ createdBy }) => {
          return <div style={{ textAlign: 'center' }}>{createdBy}</div>;
        },
      },
      {
        name: 'Posted On',
        key: 'postedOn',
        textAlign: 'center',
        width: { sm: 3, lg: 3 },
        render: ({ createdOn }) => <div style={{ textAlign: 'center' }}>{createdOn}</div>,
      },
      {
        name: 'Signed At',
        key: 'signedAt',
        textAlign: 'center',
        width: { sm: 3, lg: 3 },
        render: ({ signedAt, signature }) => (
          <div style={{ textAlign: 'center' }}>
            {signedAt ? (
              <a href="#" onClick={() => setSignature(signature)}>
                {signedAt ? signedAt : 'N/A'}
              </a>
            ) : (
              <div>N/A</div>
            )}
          </div>
        ),
      },
      {
        name: 'Status',
        key: 'status',
        textAlign: 'center',
        width: { sm: 2, lg: 2 },
        render: ({ status }) => (
          <div
            style={{
              textAlign: 'center',
              fontWeight: 700,
              color:
                status === EmployeeStatus.COMPLETE
                  ? EmployeeStatusColor.Complete.color
                  : EmployeeStatusColor.Incomplete.color,
            }}
          >
            {status}
          </div>
        ),
      },
    ];

    const handleIncludeDeletedChange = async (checked: boolean) => {
      setIncludeDeleted(checked);
      await fetchData(checked);
    };

    return (
      <>
        <EntityList<OnboardingDocumentItem>
          title="Onboarding Documents"
          searchLabel="Search by document or file name"
          columns={columns}
          items={paginate(onboardingDocuments, RESULTS_PER_PAGE, currentPage)}
          titleProp="title"
          onSearchChange={(filter) => onboardingDocumentStore.setFilter(filter)}
          onDismiss={() => onboardingDocumentStore.clearError()}
          onRefresh={() => fetchData(includeDeleted)}
          errorMessage={onboardingDocumentStore.errorMessage}
          isLoading={onboardingDocumentStore.isLoading}
          noNewButton
          searchHeaderContent={
            <Form
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 10,
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Form.Field style={{ margin: 0 }}>
                <Form.Checkbox
                  label="Show archived documents"
                  checked={includeDeleted}
                  onChange={(_, { checked }) => handleIncludeDeletedChange(checked || false)}
                />
              </Form.Field>
            </Form>
          }
        />
        <Grid centered>
          <Grid.Row>
            <Pagination
              activePage={currentPage}
              totalPages={Math.ceil(onboardingDocuments.length / RESULTS_PER_PAGE)}
              onPageChange={(_, { activePage }) => setCurrentPage(activePage as number)}
              siblingRange={2}
              boundaryRange={0}
              firstItem={{ content: 'First' }}
              lastItem={{ content: 'Last' }}
              ellipsisItem={null}
              prevItem={null}
              nextItem={null}
            />
          </Grid.Row>
        </Grid>

        {signature && (
          <SignatureModal
            visible={!!signature}
            signature={signature}
            onClose={() => setSignature(undefined)}
          />
        )}
      </>
    );
  },
);
